import React, { createContext, useContext, useEffect } from "react";
import { Group, GroupOperationResult, GroupWithId } from "../interfaces/IGroups";
import { ParticipantWithId } from "../interfaces/IParticipants";
import { useGroups } from "../hooks/useGroups";

interface GroupsContextType {
  // Stan
  groups: GroupWithId[];
  isLoading: boolean;

  // Operacje
  handleGroupAdd: (groupData: Omit<Group, "userId">) => Promise<GroupOperationResult>;
  handleGroupUpdate: (groupId: string, updatedData: Partial<Group>) => Promise<GroupOperationResult>;
  handleGroupDelete: (groupId: string) => Promise<GroupOperationResult>;
  refreshGroups: () => Promise<void>;

  // Pomocnicze
  getGroupById: (groupId: string) => GroupWithId | undefined;
  getParticipantsByGroupId: (
    groupId: string,
    allParticipants: ParticipantWithId[]
  ) => ParticipantWithId[];
}

const GroupsContext = createContext<GroupsContextType | undefined>(undefined);

export default function GroupsProvider({
  children,
  userId,
}: {
  children: React.ReactNode;
  userId: string;
}) {
  // Hook robi całą robotę - DESTRUKTURYZACJA
  const {
    groups,
    isLoading,
    handleGroupAdd,
    handleGroupUpdate,
    handleGroupDelete,
    refreshGroups,
    getGroupById,
    getParticipantsByGroupId,
  } = useGroups(userId);

  // Walidacja spójności: sprawdź czy grupy należą do aktualnego użytkownika
  useEffect(() => {
    if (groups.length > 0) {
      const invalidGroups = groups.filter((group) => group.userId !== userId);
      if (invalidGroups.length > 0) {
        console.warn(
          `🚨 [GroupsContext] Niekonsystencja danych: znaleziono ${invalidGroups.length} grup należących do innych użytkowników`
        );
        console.warn(
          "Nieprawidłowe grupy:",
          invalidGroups.map((g) => ({ id: g.id, userId: g.userId, expectedUserId: userId }))
        );
      }
    }
  }, [groups, userId]);

  return (
    <GroupsContext.Provider
      value={{
        groups,
        isLoading,
        handleGroupAdd,
        handleGroupUpdate,
        handleGroupDelete,
        refreshGroups,
        getGroupById,
        getParticipantsByGroupId,
      }}
    >
      {children}
    </GroupsContext.Provider>
  );
}

export const useGroupsContext = (): GroupsContextType => {
  const context = useContext(GroupsContext);
  if (context === undefined) {
    throw new Error("useGroupsContext must be used within a GroupsProvider");
  }
  return context;
};

